<template>
  <!-- Суммы -->
  <div class="order-sums">
    <div class="sum-row">
      <span class="sum-label">Сумма заказа</span>
      <span class="sum-value">{{ formatPrice(basketAmount) }}</span>
    </div>

    <!-- Сервисный сбор (поддержка обоих форматов) -->
    <div v-if="serviceFee > 0" class="sum-row">
      <span class="sum-label">Сервисный сбор</span>
      <span class="sum-value">{{ formatPrice(serviceFee) }}</span>
    </div>

    <!-- Доставка (поддержка обоих форматов) -->
    <div v-if="deliveryCost > 0" class="sum-row">
      <span class="sum-label">Доставка</span>
      <span class="sum-value">{{ formatPrice(deliveryCost) }}</span>
    </div>

    <!-- Приветственная скидка на первые заказы -->
    <div v-if="onboardingDiscount > 0" class="sum-row discount">
      <span class="sum-label">Скидка на первые заказы</span>
      <span class="sum-value">−{{ formatPrice(onboardingDiscount) }}</span>
    </div>

    <!-- Скидка по промокоду -->
    <div v-if="promocodeDiscount > 0" class="sum-row discount">
      <span class="sum-label">Скидка по промокоду</span>
      <span class="sum-value">−{{ formatPrice(promocodeDiscount) }}</span>
    </div>

    <!--
      Подарок отдельной строкой — только если его не удалось привязать к позиции
      (старый бэкенд без пометки или подарочного товара уже нет в заказе).
      Когда строка помечена, скидка уже вычтена в ней и в «Сумме заказа».
    -->
    <div v-if="giftDiscount > 0" class="sum-row discount" data-testid="order-gift-discount">
      <span class="sum-label">Подарок</span>
      <span class="sum-value">−{{ formatPrice(giftDiscount) }}</span>
    </div>

    <!-- Списание баллов (поддержка обоих форматов) -->
    <div v-if="pointsUsed > 0" class="sum-row discount">
      <span class="sum-label">Списание баллов</span>
      <span class="sum-value">−{{ formatPrice(pointsUsed) }}</span>
    </div>

    <!-- Кешбэк баллами (информационно, в итог не входит) -->
    <div v-if="pointsEarned > 0" class="sum-row discount">
      <span class="sum-label">Вернётся баллами</span>
      <span class="sum-value">+{{ formatPrice(pointsEarned) }}</span>
    </div>

    <div class="sum-row total">
      <span>Итого</span>
      <span>{{ formatPrice(totalAmount) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  calculateOrderTotal,
  formatPrice,
  getAttributedGiftDiscount,
  getUnattributedGiftDiscount,
} from '@entities/order'
import type { Order } from '@entities/order'

interface Props {
  order: Order | null
}

const props = defineProps<Props>()

/**
 * Сумма заказа по позициям — за вычетом подарка, уже разнесённого по строкам.
 *
 * Подарочное блюдо лежит в позициях по полной цене, но гость за него не платит,
 * поэтому в «Сумме заказа» оно учитывается по цене к оплате — ровно так же, как
 * показано в самой строке.
 */
const basketAmount = computed(() => {
  if (!props.order?.items) return 0
  return calculateOrderTotal(props.order.items) - getAttributedGiftDiscount(props.order.items)
})

/**
 * Сервисный сбор (поддержка обоих форматов: snake_case и camelCase)
 */
const serviceFee = computed(() => {
  const order = props.order as any
  return order?.serviceFee || order?.service_fee || 0
})

/**
 * Стоимость доставки (поддержка обоих форматов)
 */
const deliveryCost = computed(() => {
  const order = props.order as any
  return order?.deliveryCost || order?.delivery_cost || 0
})

/**
 * Использованные баллы (поддержка обоих форматов)
 */
const pointsUsed = computed(() => {
  const order = props.order as any
  return order?.pointsUsed || order?.points_used || 0
})

/**
 * Скидка по промокоду (поддержка обоих форматов)
 */
const promocodeDiscount = computed(() => {
  const order = props.order as any
  return order?.promocodeDiscount || order?.promocode_discount || 0
})

/**
 * Приветственная скидка на первые заказы (поддержка обоих форматов)
 */
const onboardingDiscount = computed(() => {
  const order = props.order as any
  return order?.onboardingDiscount || order?.onboarding_discount || 0
})

/**
 * Скидка за подарок — за сумму заказа или за частоту заказов (поддержка обоих форматов).
 *
 * Подарочное блюдо лежит в позициях по ПОЛНОЙ цене: так его видит касса. Без этой
 * строки детализация не сходилась с итогом ровно на цену подарка, и выглядело это
 * так, будто с гостя взяли за подарок деньги (заказ #7829: 1550 + 43 − 81 = 1512
 * против фактических 1462).
 */
const giftDiscount = computed(() => {
  const order = props.order as any
  const total = order?.giftTierDiscount || order?.gift_tier_discount || 0
  return getUnattributedGiftDiscount(total, props.order?.items ?? [])
})

/**
 * Баллы к начислению (кешбэк), поддержка обоих форматов
 */
const pointsEarned = computed(() => {
  const order = props.order as any
  return order?.pointsEarned || order?.points_earned || 0
})

/**
 * Итоговая сумма (поддержка обоих форматов)
 */
const totalAmount = computed(() => {
  const order = props.order as any
  return order?.totalAmount || order?.total_amount || 0
})
</script>

<style scoped lang="scss">
.order-sums {
  @apply grid gap-1.5 bg-hui-content2 rounded-hui-lg p-4;
}

.sum-row {
  @apply flex justify-between items-baseline gap-3 text-sm;

  .sum-label {
    @apply text-hui-default-500;
  }

  .sum-value {
    @apply font-medium text-hui-foreground whitespace-nowrap;
  }

  &.discount .sum-value {
    @apply text-hui-success-600;
  }
}

.total {
  @apply font-bold text-xl text-hui-foreground mt-2 pt-3 border-t border-hui-default-200;
}
</style>